import { css, cx } from '@linaria/core'
import { clamp } from 'lodash'
import type { FC, HTMLProps, MouseEventHandler } from 'react'
import { Icon } from '#components/Icon'
import ChevronLeft from '#icons/chevron-1-left-24.svg'
import ChevronRight from '#icons/chevron-1-right-24.svg'
import { style } from '#styles/index'

const indicatorClass = css`
  align-self: stretch;
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: flex-end;
  padding: 8px;
  justify-content: space-between;

  > .action {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: 0.25s ease coler;

    &:hover {
      color: ${style.themeColors.textImportant};
    }

    &.disabled {
      cursor: unset;
      color: ${style.themeColors.textDisabled}
    }
  }

  > .indicator {
    cursor: pointer;
    flex: 1;
    display: flex;
    align-items: center;
    border-radius: 5px;
    // 11px up, 2px height, 11px down -> 24px total height
    padding: 11px 0;

    > .bar {
      flex: 1;
      height: 2px;
      border-radius: 5px;
      background-color: ${style.themeColors.lineDefault};
      transition: 0.25s ease background-color;
    }

    &.selected {
      cursor: unset;
      > .bar {
        background-color: ${style.themeColors.textImportant};
      }
    }
  }
`

export const Indicator: FC<
  {
    withArrows?: boolean
    onPreviousRequested?: MouseEventHandler<HTMLDivElement>
    onNextRequested?: MouseEventHandler<HTMLDivElement>
    onIndicatorClicked?: (evt: React.MouseEvent<HTMLDivElement>, i: number) => void
    currentIndex: number
    itemCount: number
  } & HTMLProps<HTMLDivElement>
> = ({ withArrows = true, onPreviousRequested, onNextRequested, onIndicatorClicked, currentIndex, itemCount, ref, className }) => {
  const index = clamp(currentIndex, 0, itemCount - 1)
  const itemIndices = [...Array(itemCount).keys()] as const

  return (
    <div ref={ref} className={cx(indicatorClass, 'indicator', className)}>
      {withArrows && (
        <div className={cx('action', 'previous', index <= 0 && 'disabled')} onClick={onPreviousRequested}>
          <Icon className="icon" size="m" iconUrl={ChevronLeft} />
        </div>
      )}

      {itemIndices.map((x) => (
        <div key={x} onClick={(evt) => onIndicatorClicked?.(evt, x)} className={cx('indicator', index === x && 'selected')}>
          <div className="bar" />
        </div>
      ))}

      {withArrows && (
        <div className={cx('action', 'next', index >= itemCount - 1 && 'disabled')} onClick={onNextRequested}>
          <Icon className="icon" size="m" iconUrl={ChevronRight} />
        </div>
      )}
    </div>
  )
}
